/**
 * useQuota Hook - Dynamic usage tracking (2025-11-28)
 *
 * Separate from useEntitlements: different refresh cadence, lower re-render churn.
 * Takes quotaPolicy from useEntitlements, computes remaining = limit - used.
 * Replaces: useQuotaUsage (deprecated), useUniversalQuota
 *
 * Diverges from apps/mobile/hooks/useQuota.ts ONLY by omitting the __DEV__
 * Detox override block (apps/web-landing has no lib/dev/hook-overrides). All
 * other logic MUST stay byte-identical to the mobile version.
 */
import { useQuery } from '@tanstack/react-query';
import { getDeviceId } from '../lib/device';
import { logNetworkError } from '../lib/logNetworkError';
import { fetchJsonValidated, getAuthStatus, useAuthReady } from '@dramadetector/api-client';
import { AnonymousQuotaDto, FamilyQuotaDto, QuotaUsageDto } from '@dramadetector/shared';
import * as Sentry from '@sentry/react-native';
import { getEdgeFunctionUrl } from '../lib/api-url';
import type { QuotaPolicy } from './useEntitlements';
import { useEntitlements } from './useEntitlements';

function resolveResetAt(storedResetAt: string | undefined | null): string {
  const now = new Date();
  const nextMidnight = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  if (storedResetAt) {
    const stored = new Date(storedResetAt);
    if (stored > now) return storedResetAt;
  }
  return nextMidnight.toISOString();
}

/** Quota usage data (remaining is computed from quotaPolicy.limit - used) */
export interface QuotaUsage {
  used: number;
  resetAt: string; // ISO timestamp
}

export interface UseQuotaReturn {
  // Usage data
  used: number;
  remaining: number;
  resetAt: string;

  // Derived values
  usagePercent: number;
  formattedResetTime: string;
  isExhausted: boolean;

  // Loading state
  isLoading: boolean;
  error: Error | null;

  // Refresh function
  refetch: () => void;
}

// Query key factory for quota usage
export const quotaKeys = {
  all: ['quota'] as const,
  usage: (scope: string, scopeId: string) =>
    [...quotaKeys.all, 'usage', scope, scopeId] as const,
};

/** Format reset time for display */
function formatResetTime(resetAt: string, window: 'daily' | 'weekly'): string {
  const resetDate = new Date(resetAt);
  const now = new Date();

  // Format the reset time with timezone
  const timeStr = resetDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  if (window === 'weekly') {
    // For weekly, show day of week + time
    const daysUntilReset = Math.ceil(
      (resetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysUntilReset <= 0) {
      return `today at ${timeStr}`;
    }
    if (daysUntilReset === 1) {
      return `tomorrow at ${timeStr}`;
    }
    return `in ${daysUntilReset} days (${timeStr})`;
  }

  // For daily, show time
  const hoursUntilReset = Math.ceil(
    (resetDate.getTime() - now.getTime()) / (1000 * 60 * 60)
  );
  if (hoursUntilReset <= 1) {
    return `in less than 1 hour (${timeStr})`;
  }
  if (hoursUntilReset < 24) {
    return `in ${hoursUntilReset} hours (${timeStr})`;
  }
  return `tomorrow at ${timeStr}`;
}

/** Dynamic quota usage tracking. Takes quotaPolicy from useEntitlements(). */
export function useQuota(quotaPolicy: QuotaPolicy | null): UseQuotaReturn {

  const scope = quotaPolicy?.scope ?? 'device';
  const scopeId = quotaPolicy?.scopeId ?? '';
  const window = quotaPolicy?.window ?? 'daily';
  const limit = quotaPolicy?.limit ?? 10;

  // Gate: wait for real scopeId + auth token before firing (Sentry #7391510004)
  const authReady = useAuthReady();
  const isAuthReady = scope === 'device' || authReady;
  const isQueryEnabled = !!quotaPolicy && !!scopeId && isAuthReady;

  // Fetch quota usage from backend
  const {
    data: usageData,
    isLoading: isQueryLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: quotaKeys.usage(scope, scopeId),
    queryFn: async (): Promise<QuotaUsage> => {
      const isAnonymous = scope === 'device';
      const deviceId = isAnonymous ? (scopeId || (await getDeviceId())) : undefined;

      const url = new URL(getEdgeFunctionUrl('get-quota'));
      url.searchParams.set('scope', scope === 'device' ? 'anonymous' : scope);

      // Phase 2 (NocoDB close-out): drop `skipAuth: true` for anon callers.
      // Anon BA users have a Bearer JWT; analyze-drama writes quota keyed on
      // the BA sub (from the JWT). If useQuota sends only X-Device-Id (via
      // skipAuth), the server derives identity from the header (local device
      // id) and reads an EMPTY ledger — so the "10/10 remaining" counter
      // stays frozen even after submissions. Silent desync.
      //
      // Mirrors the useTier / useEntitlements fix in PR #1007. When Phase 2
      // bootstrap is ON, every caller has a Bearer → server uses the BA sub
      // consistently across reads AND writes. When Phase 2 is OFF, there is
      // no Bearer and `status === 'anonymous'`; authenticatedFetch then
      // attaches X-Device-Id via its own path — but we also attach it here
      // explicitly for the pre-bootstrap window where authenticatedFetch's
      // status check hasn't flipped yet.
      const fetchOptions: RequestInit = {};
      if (isAnonymous) {
        fetchOptions.headers = { 'X-Device-Id': deviceId! };
      }

      // Sentry breadcrumb: quota fetch context for auth timing diagnostics (#3078)
      Sentry.addBreadcrumb({
        category: 'quota',
        message: `get-quota scope=${scope}`,
        level: isAnonymous ? 'info' : 'warning',
        data: { scope, scopeId, authStatus: getAuthStatus(), isAnonymous },
      });

      try {
        // Validated against the shared DTO contract — no field fallbacks.
        // Schema drift surfaces as an immediate runtime error (not silent 0s).
        const dto = isAnonymous
          ? await fetchJsonValidated(AnonymousQuotaDto, url.toString(), fetchOptions)
          : scope === 'family'
            ? await fetchJsonValidated(FamilyQuotaDto, url.toString(), fetchOptions)
            : await fetchJsonValidated(QuotaUsageDto, url.toString(), fetchOptions);
        return { used: dto.used, resetAt: dto.resetAt };
      } catch (err) {
        logNetworkError(err as Error, {
          hook: 'useQuota',
          endpoint: 'get-quota',
          isAuthenticated: !isAnonymous,
        });
        // Graceful fallback for authenticated users: return zero usage so the UI unblocks.
        if (!isAnonymous) {
          return { used: 0, resetAt: resolveResetAt(null) };
        }
        throw err;
      }
    },
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    enabled: isQueryEnabled,
    refetchOnMount: 'always', // Cross-tab accuracy (2026-01-15)
    retry: 1, // Single retry; persistent failures use queryFn defaults (2026-03-29)
  });

  // Realtime removed (2026-04-02): TODO (#2510) replace with polling/WebSocket if needed.

  // Derived values: show defaults (not spinner) when query is disabled (2026-03-29)
  const isLoading = isQueryEnabled && isQueryLoading;
  const used = usageData?.used ?? 0;
  const remaining = usageData
    ? Math.max(0, limit - used)   // Real data: compute actual remaining
    : limit;                       // No data yet: show full quota as default
  const usagePercent = usageData
    ? (limit > 0 ? Math.round((used / limit) * 100) : 0)
    : 0;
  const resetAt = usageData?.resetAt ?? new Date().toISOString();
  const formattedResetTime = formatResetTime(resetAt, window);
  const isExhausted = !!usageData && remaining <= 0; // Only exhausted if we have real data

  // Note: apps/mobile/hooks/useQuota.ts has an additional __DEV__-guarded Detox
  // override block here (reads from ../lib/dev/hook-overrides). apps/web-landing
  // has no Detox infrastructure, so the block is intentionally omitted.

  return {
    used,
    remaining,
    resetAt,
    usagePercent,
    formattedResetTime,
    isExhausted,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

/** Combined hook: returns both entitlements + quota. Prefer separate hooks for re-render optimization. */
export function useQuotaWithEntitlements() {
  const entitlements = useEntitlements();
  const quota = useQuota(entitlements.quotaPolicy);

  return {
    ...entitlements,
    ...quota,
    // Explicit naming to avoid confusion
    quotaUsed: quota.used,
    quotaRemaining: quota.remaining,
    quotaResetAt: quota.resetAt,
  };
}
